import type { FieldValue, PresenceUser } from '@colplat/protocol';
import type { MoneyCurrency } from '@colplat/ui-web';
import { formatAmount, formatNum, fromScaled, scalarString, toScaled } from '@colplat/ui-web';

/**
 * Turning a transaction's commit history into something a person can read.
 *
 * The pure half lives here — labelling, diffing, folding state forward,
 * checking the balance — because every one of those is a place to be quietly
 * wrong: a mislabelled field, a split attributed to the wrong row, money
 * summed through a float. The DOM half is in webledgerTransactions.ts.
 *
 * The shape mirrors `/apps/webledger/transactions/:id/history`, which groups
 * `field_history` by HLC because that is what a commit is: one save stamps
 * the parent and every split with the same clock.
 */

export interface HistoryChange {
  entity: string; recordId: string; field: string;
  /** `null` on a creation — there was nothing before it. */
  from: FieldValue | null;
  to: FieldValue;
}

export interface HistoryEvent { entity: string; recordId: string; kind: string }

export interface HistoryCommit {
  id: string;
  wallTime: string;
  author: PresenceUser | null;
  source: 'online' | 'offline' | 'external' | 'import';
  /** Written on top of a version that had already moved (ADR-0005). */
  staleBase: boolean;
  changes: HistoryChange[];
  events: HistoryEvent[];
}

export interface HistoryRevision {
  id: string; reason: string; createdAt: string;
  resolvedAt: string | null; resolution: 'applied' | 'discarded' | null;
  author: PresenceUser | null;
}

export interface TransactionHistory {
  commits: HistoryCommit[];
  revisions: HistoryRevision[];
  /**
   * Every split this history mentions, live or removed, at the version it is
   * at now. The server sends it because a split removed since is not in the
   * transaction any more, and restoring a version that had it means writing
   * to it — which needs its version (invariant #2). Optional so an older
   * cached history still parses.
   */
  splits?: Array<{ id: string; version: number; deleted: boolean }>;
}

export interface HistoryContext {
  accountName: (id: string) => string;
  /**
   * Names for the other things history refers to by id.
   *
   * Without these a change reads "Tag: — → 01a0d4e9-ef6a-7a35-…", which tells
   * the reader nothing at all: the id is the one part of the record that
   * means nothing to a person. Optional so a caller that has not loaded them
   * degrades to the id rather than throwing.
   */
  tagName?: (id: string) => string | undefined;
  walletName?: (id: string) => string | undefined;
  currency: (code: string) => MoneyCurrency | undefined;
  /** The transaction's own currency — what a split's `value` is denominated in. */
  txCurrencyCode: string;
  /** Stable 1-based numbering per split record, from `splitNumbering`. */
  splitNumber: ReadonlyMap<string, number>;
  /**
   * Tag-link record id → the tag it points at, from `tagLinkTargets`.
   *
   * Removing a tag writes a record event and no field values (a delete has
   * nothing to say about fields), so the commit that removes one does not
   * itself contain the tag. The commit that *created* the link does, and
   * history is append-only, so it is still there to be read.
   */
  tagOfLink?: ReadonlyMap<string, string>;
}

/**
 * Number the splits in the order they first appear, oldest commit first.
 *
 * Splits need a stable label that survives having their account changed —
 * naming a split by its account would make "Account: Cash → Bank" read as two
 * different splits rather than one changing. The number never moves; the
 * account is shown alongside it.
 */
export function splitNumbering(commits: readonly HistoryCommit[]): Map<string, number> {
  const numbers = new Map<string, number>();
  // The API returns newest first; numbering must follow creation order.
  for (const commit of [...commits].reverse()) {
    for (const change of commit.changes) {
      if (change.entity !== 'split') continue;
      if (!numbers.has(change.recordId)) numbers.set(change.recordId, numbers.size + 1);
    }
    for (const event of commit.events) {
      if (event.entity !== 'split') continue;
      if (!numbers.has(event.recordId)) numbers.set(event.recordId, numbers.size + 1);
    }
  }
  return numbers;
}

/**
 * Tag-link record id → the tag id it was created pointing at.
 *
 * Built over the whole timeline for the same reason as `splitNumbering`: the
 * commit in front of the reader is rarely the one that holds the answer.
 */
export function tagLinkTargets(commits: readonly HistoryCommit[]): Map<string, string> {
  const targets = new Map<string, string>();
  for (const commit of [...commits].reverse()) {
    for (const change of commit.changes) {
      if (change.entity !== 'transactionTag' || change.field !== 'tagId') continue;
      if (change.to.v !== null) targets.set(change.recordId, scalarText(change.to.v));
    }
  }
  return targets;
}

/**
 * A `FieldValue`'s payload as text.
 *
 * `scalarText(value.v)` stopped being safe when the `json` field type arrived
 * (ADR-0026): `v` is `unknown`, so anything that is not a scalar rendered as
 * "[object Object]" instead of being refused. Every value this file reaches
 * for by name is a scalar; a `json` one has its own renderer and never comes
 * through here.
 */
function scalarText(raw: unknown): string {
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
  return '';
}

const TRANSACTION_LABELS: Record<string, string> = {
  legs: 'Splits',
  txDate: 'Date',
  reference: 'Reference',
  description: 'Description',
  currencyCode: 'Currency',
};

const SPLIT_LABELS: Record<string, string> = {
  accountId: 'Account',
  memo: 'Memo',
  value: 'Amount',
  amount: 'Account-currency amount',
  currencyCode: 'Split currency',
  walletId: 'Wallet',
};

/** A tag assignment is its own record, so its fields get their own names. */
const TAG_LINK_LABELS: Record<string, string> = {
  tagId: 'Tag',
  transactionId: 'On transaction',
};

export interface DescribedChange { label: string; from: string; to: string }

/**
 * One change as three strings: what changed, from what, to what.
 *
 * Values are rendered the way the form renders them, not as raw storage —
 * an account id means nothing to a reader, and `-469.0000` is not how this
 * app writes money.
 */
export function describeChange(
  change: HistoryChange,
  ctx: HistoryContext,
  /**
   * The split's own currency as of this commit. Required to render `amount`
   * correctly: `value` is in the transaction's currency but `amount` is in the
   * *account's*, and formatting one with the other's precision and symbol is
   * exactly the confusion the two columns exist to avoid.
   */
  splitCurrencyCode?: string,
): DescribedChange {
  const isSplit = change.entity === 'split';
  const labels = isSplit ? SPLIT_LABELS
    : change.entity === 'transactionTag' ? TAG_LINK_LABELS
      : TRANSACTION_LABELS;
  const base = labels[change.field] ?? change.field;
  const number = isSplit ? ctx.splitNumber.get(change.recordId) : undefined;
  const label = number === undefined ? base : `Split ${number} · ${base}`;

  return {
    label,
    from: change.from === null ? '—' : renderValue(change.field, change.from, ctx, splitCurrencyCode),
    to: renderValue(change.field, change.to, ctx, splitCurrencyCode),
  };
}

/** One leg, as it is stored on the transaction (ADR-0026). */
type HistoryLeg = Record<string, unknown>;

function legsOf(value: FieldValue | null): HistoryLeg[] {
  if (value === null || value.t === 'null') return [];
  const raw = value.v;
  if (!Array.isArray(raw)) return [];
  return raw.filter((leg): leg is HistoryLeg => typeof leg === 'object' && leg !== null);
}

function legText(leg: HistoryLeg, key: string): string | undefined {
  const raw = leg[key];
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number') return String(raw);
  return undefined;
}

/** A leg's field, rendered the way the register renders it. */
function renderLegField(
  field: string, raw: string | undefined, leg: HistoryLeg, ctx: HistoryContext,
): string {
  if (raw === undefined || raw === '') return '—';
  if (field === 'accountId') return ctx.accountName(raw);
  if (field === 'walletId') return ctx.walletName?.(raw) ?? raw;
  // `value` is in the TRANSACTION's currency; `amount` is in the leg's own.
  // Formatting either with the other's is the confusion the two exist to
  // avoid, and the reason this takes the leg rather than just the field.
  if (field === 'value') return formatNum(raw, ctx.currency(ctx.txCurrencyCode));
  if (field === 'amount') {
    return formatAmount(raw, ctx.currency(legText(leg, 'currencyCode') ?? ctx.txCurrencyCode));
  }
  return raw;
}

/** A whole leg on one line, for the two cases where it arrived or left. */
function legLine(leg: HistoryLeg, ctx: HistoryContext): string {
  const account = renderLegField('accountId', legText(leg, 'accountId'), leg, ctx);
  const amount = renderLegField('amount', legText(leg, 'amount'), leg, ctx);
  return amount === '—' ? account : `${account} ${amount}`;
}

/** The fields of a leg worth reporting, in the order a reader scans them. */
const LEG_FIELDS = ['accountId', 'value', 'amount', 'currencyCode', 'memo', 'walletId'] as const;

/**
 * A change to `legs`, as one line per thing that actually changed.
 *
 * Since ADR-0026 a transaction's legs are one JSON field rather than records
 * of their own, so every edit to any of them arrives here as a single change
 * carrying both whole arrays. Rendering that as one row said only what the
 * legs became — true, and useless for the question a history is opened to
 * answer, which is what somebody did.
 *
 * So the arrays are diffed by leg id and reported the way the old per-split
 * rows read: "Split 2 · Account — Cash → Safe". A leg that arrived or left is
 * one line rather than six, because that is the change.
 *
 * Numbered by position in the new array, with legs that are gone counted
 * after it — the same numbering the editor shows, and stable for the legs
 * that stayed.
 */
export function describeLegsChange(change: HistoryChange, ctx: HistoryContext): DescribedChange[] {
  const before = legsOf(change.from);
  const after = legsOf(change.to);
  const order = [
    ...after.map((leg) => legText(leg, 'id')),
    ...before.map((leg) => legText(leg, 'id')).filter((id) => !after.some((l) => legText(l, 'id') === id)),
  ].filter((id): id is string => id !== undefined);

  const out: DescribedChange[] = [];
  order.forEach((id, index) => {
    const was = before.find((leg) => legText(leg, 'id') === id);
    const now = after.find((leg) => legText(leg, 'id') === id);
    const n = index + 1;
    if (!was && now) { out.push({ label: `Split ${n} · added`, from: '—', to: legLine(now, ctx) }); return; }
    if (was && !now) { out.push({ label: `Split ${n} · removed`, from: legLine(was, ctx), to: '—' }); return; }
    if (!was || !now) return;
    for (const field of LEG_FIELDS) {
      const from = legText(was, field);
      const to = legText(now, field);
      if ((from ?? '') === (to ?? '')) continue;
      out.push({
        label: `Split ${n} · ${SPLIT_LABELS[field] ?? field}`,
        from: renderLegField(field, from, was, ctx),
        to: renderLegField(field, to, now, ctx),
      });
    }
  });
  return out;
}

/** Nothing to nothing is not a change — a field created empty says nothing to a reader. */
export function isVisibleChange(described: DescribedChange): boolean {
  return !(described.from === '—' && described.to === '—');
}

function renderValue(
  field: string, value: FieldValue, ctx: HistoryContext, splitCurrencyCode?: string,
): string {
  if (value.t === 'null' || value.v === null || value.v === '') return '—';
  if (field === 'accountId') return ctx.accountName(scalarText(value.v));
  // An id is the one part of a record that means nothing to a reader. Fall
  // back to it only when the name cannot be found — a tag deleted since, say,
  // where showing the id at least says "something was here".
  if (field === 'tagId') return ctx.tagName?.(scalarText(value.v)) ?? scalarText(value.v);
  if (field === 'walletId') return ctx.walletName?.(scalarText(value.v)) ?? scalarText(value.v);
  if (field === 'value') {
    // A split's `value` is in the *transaction's* currency, and its decimals
    // come from that currency's own `fraction` — never the stored scale.
    return formatNum(scalarText(value.v), ctx.currency(ctx.txCurrencyCode));
  }
  if (field === 'amount') {
    // ...whereas `amount` is in the split's own currency. Formatting it with
    // the transaction's produced things like `-Ft.469.00` for a EUR split.
    return formatAmount(scalarText(value.v), ctx.currency(splitCurrencyCode ?? ctx.txCurrencyCode));
  }
  if (field === 'legs') return renderLegs(value.v, ctx);
  return scalarText(value.v);
}

/**
 * A transaction's legs, as a line somebody can read.
 *
 * Since ADR-0026 the legs are one JSON field of the transaction rather than
 * records of their own, so a change to any of them arrives here as a whole
 * array — and the fall-through below turned that into
 * "[object Object],[object Object]", which is what a reader saw after
 * changing an account.
 *
 * Each leg is named by its account and its own amount, and both sides of the
 * change render the same way, so "from" and "to" can be compared by eye.
 * That is deliberately a *summary* rather than a diff: a per-leg diff is
 * worth having and is a larger piece of work, and until then this says what
 * the legs became instead of saying nothing.
 */
function renderLegs(raw: unknown, ctx: HistoryContext): string {
  if (!Array.isArray(raw)) return '—';
  if (raw.length === 0) return '—';
  return raw.map((leg) => {
    if (typeof leg !== 'object' || leg === null) return '?';
    const one = leg as Record<string, unknown>;
    const accountId = typeof one['accountId'] === 'string' ? one['accountId'] : '';
    const code = typeof one['currencyCode'] === 'string' ? one['currencyCode'] : undefined;
    // `amount` is in the leg's OWN currency, so it is formatted with that
    // one — the same distinction `amount` above makes against `value`.
    const amount = typeof one['amount'] === 'string' || typeof one['amount'] === 'number'
      ? formatAmount(String(one['amount']), ctx.currency(code ?? ctx.txCurrencyCode))
      : '';
    const name = accountId ? ctx.accountName(accountId) : '?';
    return amount ? `${name} ${amount}` : name;
  }).join(' · ');
}

/** A one-line summary, so the timeline is scannable before anything is expanded. */
export function summariseCommit(commit: HistoryCommit, ctx: HistoryContext): string {
  const created = commit.events.filter((e) => e.kind === 'created');
  const deleted = commit.events.filter((e) => e.kind === 'deleted');

  if (created.some((e) => e.entity === 'transaction')) {
    const splits = created.filter((e) => e.entity === 'split').length;
    return splits > 0
      ? `created, with ${splits} split${splits === 1 ? '' : 's'}`
      : 'created';
  }

  const parts: string[] = [];
  const parentFields = commit.changes.filter((c) => c.entity === 'transaction');
  const splitRecords = new Set(commit.changes.filter((c) => c.entity === 'split').map((c) => c.recordId));

  if (parentFields.length === 1) {
    parts.push(`changed the ${(TRANSACTION_LABELS[parentFields[0]!.field] ?? parentFields[0]!.field).toLowerCase()}`);
  } else if (parentFields.length > 1) {
    parts.push(`changed ${parentFields.length} fields`);
  }
  if (splitRecords.size > 0) {
    const numbers = [...splitRecords].map((id) => ctx.splitNumber.get(id)).filter((n): n is number => n !== undefined);
    parts.push(numbers.length === 1 ? `edited split ${numbers[0]}` : `edited ${splitRecords.size} splits`);
  }
  if (created.some((e) => e.entity === 'split')) {
    const n = created.filter((e) => e.entity === 'split').length;
    parts.push(`added ${n} split${n === 1 ? '' : 's'}`);
  }
  if (deleted.some((e) => e.entity === 'split')) {
    const n = deleted.filter((e) => e.entity === 'split').length;
    parts.push(`removed ${n} split${n === 1 ? '' : 's'}`);
  }
  // A restore is its own kind of event, not an add: the split is the same
  // record with the same number, which is exactly what makes "brought back"
  // the honest word for it.
  const revived = commit.events.filter((e) => e.entity === 'split' && e.kind === 'restored');
  if (revived.length > 0) {
    const numbers = revived.map((e) => ctx.splitNumber.get(e.recordId)).filter((n): n is number => n !== undefined);
    parts.push(numbers.length === 1
      ? `brought split ${numbers[0]} back`
      : `brought ${revived.length} splits back`);
  }
  // Tag links are records like any other, so without this a commit that only
  // tagged something falls through to "saved" — accurate and useless. The tag
  // is named rather than counted when there is one, because "added the tag
  // Groceries" is the whole content of that commit.
  const tagged = created.filter((e) => e.entity === 'transactionTag');
  const untagged = deleted.filter((e) => e.entity === 'transactionTag');
  if (tagged.length > 0) parts.push(`added ${namedTags(tagged, commit, ctx)}`);
  if (untagged.length > 0) parts.push(`removed ${namedTags(untagged, commit, ctx)}`);
  if (deleted.some((e) => e.entity === 'transaction')) return 'deleted';

  return parts.length > 0 ? parts.join(', ') : 'saved';
}

/**
 * "the tag Groceries" for one, "2 tags" for several.
 *
 * The name has to be dug out of the commit's own changes: a `transactionTag`
 * record is a pair of ids, so the event alone says only that one appeared.
 */
function namedTags(
  events: HistoryEvent[], commit: HistoryCommit, ctx: HistoryContext,
): string {
  if (events.length > 1) return `${events.length} tags`;
  const link = events[0]!.recordId;
  const which = commit.changes.find((c) => c.entity === 'transactionTag' && c.recordId === link && c.field === 'tagId');
  const tagId = which && which.to.v !== null ? scalarText(which.to.v) : ctx.tagOfLink?.get(link);
  const name = tagId === undefined ? undefined : ctx.tagName?.(tagId);
  return name ? `the tag ${name}` : 'a tag';
}

export interface Snapshot {
  /** Parent field values as of the chosen commit. */
  fields: Record<string, FieldValue>;
  /** Split record id → its field values, excluding any removed by then. */
  splits: Map<string, Record<string, FieldValue>>;
}

/**
 * The whole transaction as it stood immediately after a given commit.
 *
 * Reconstructed by folding forward from creation, which is only possible
 * because history now records changes rather than every field of every save —
 * the same property that makes the diff readable makes this exact.
 */
export function foldSnapshot(commits: readonly HistoryCommit[], commitId: string): Snapshot {
  const snapshot: Snapshot = { fields: {}, splits: new Map() };
  const oldestFirst = [...commits].reverse();

  for (const commit of oldestFirst) {
    for (const change of commit.changes) {
      if (change.entity === 'transaction') {
        snapshot.fields[change.field] = change.to;
      } else if (change.entity === 'split') {
        const split = snapshot.splits.get(change.recordId) ?? {};
        split[change.field] = change.to;
        snapshot.splits.set(change.recordId, split);
      }
      // Anything else is a child of the transaction that is not part of its
      // *shape* — a tag link, today. Named rather than left to an `else`,
      // which is how tag links landed in `splits` the day the history began
      // carrying them: a snapshot then held records with no `value` and no
      // `accountId`, every tag commit reported itself unrestorable because
      // its "split" was not among the transaction's, and nothing said why.
    }
    // Removals apply after this commit's own writes: a commit can both write
    // a split's fields and delete it, and gone wins.
    for (const event of commit.events) {
      if (event.kind === 'deleted' && event.entity === 'split') snapshot.splits.delete(event.recordId);
    }
    if (commit.id === commitId) break;
  }
  return snapshot;
}

/**
 * Sum a snapshot's split values, exactly.
 *
 * Scaled integer arithmetic on `BigInt`, never floats: this is the number
 * that decides whether a transaction balances, and `0.1 + 0.2` is the classic
 * way to make a ledger disagree with itself. Returns a decimal string so the
 * caller can format it against the transaction's currency.
 */
export function balanceOf(snapshot: Snapshot, scale = 4): string {
  let total = 0n;
  for (const split of snapshot.splits.values()) {
    const value = split['value'];
    if (!value || value.t === 'null' || value.v === null) continue;
    total += toScaled(scalarText(value.v), scale);
  }
  return fromScaled(total, scale);
}

/** True when a snapshot's splits do not sum to zero — should be impossible, so worth surfacing. */
export function isUnbalanced(snapshot: Snapshot, scale = 4): boolean {
  return snapshot.splits.size > 0 && toScaled(balanceOf(snapshot, scale), scale) !== 0n;
}

// ------------------------------------------------------------- rendering

/**
 * The timeline, as markup. Kept in this module beside the logic it renders
 * rather than in the 1900-line view, and deliberately a pure
 * `data -> string` function: the panel is re-rendered wholesale on every
 * change, which is safe precisely because it contains nothing the user is
 * typing into.
 */
export interface TimelineOptions {
  history: TransactionHistory;
  ctx: HistoryContext;
  /** False when the viewer cannot write this record — no restore offered at all. */
  canRestore: boolean;
  /** The splits as they stand now, so a version that cannot be restored says so instead of failing on click. */
  currentSplits?: readonly CurrentSplit[];
  /**
   * The commit the form is currently showing via "View as of here", marked so
   * it is obvious which past version is on screen — the form itself looks the
   * same whichever version it holds.
   */
  viewingCommitId?: string;
  escapeHtml: (raw: string) => string;
  presenceDotHtml: (user: PresenceUser) => string;
  timeAgo: (iso: string) => string;
}

export function timelineHtml(opts: TimelineOptions): string {
  const { history, ctx, escapeHtml: esc, presenceDotHtml: dot, timeAgo } = opts;
  if (history.commits.length === 0) {
    return '<p class="text-secondary small mb-0">No changes recorded for this transaction.</p>';
  }

  const revisions = history.revisions.map((rev) => {
    const who = rev.author ? esc(rev.author.name) : 'Someone';
    const outcome = rev.resolution === 'applied' ? 'and was applied'
      : rev.resolution === 'discarded' ? 'and was discarded'
      : '\u2014 still waiting for a decision';
    return `<div class="cf-tl is-import"><span class="cf-tl__dot"></span>
      <div class="cf-tl__who">${who}\u2019s offline edit was held for review</div>
      <div class="cf-tl__when">${esc(timeAgo(rev.createdAt))} \u00b7 ${esc(rev.reason)} ${outcome}</div></div>`;
  }).join('');

  const entries = history.commits.map((commit, index) => {
    const isCurrent = index === 0;
    const isViewing = opts.viewingCommitId === commit.id;
    const viewingChip = isViewing
      ? '<span class="cf-hist__cur" style="border-color:#2563eb;color:#2563eb">shown in the form</span>'
      : '';
    const snapshot = foldSnapshot(history.commits, commit.id);
    const unbalanced = isUnbalanced(snapshot);
    const who = commit.author
      ? `${dot(commit.author)}<strong>${esc(commit.author.name)}</strong>`
      : `<strong>${commit.source === 'import' ? 'Imported' : 'System'}</strong>`;

    const originChip = `<span class="cf-hist__origin${commit.source === 'offline' ? ' offline' : ''}">${esc(commit.source)}</span>`;
    const currentChip = isCurrent ? '<span class="cf-hist__cur">current</span>' : '';
    const staleChip = commit.staleBase
      ? '<span class="cf-hist__origin" style="border-color:#e0b072;color:#96650f" title="Written on top of a version that had already changed">stale base</span>'
      : '';

    const changeLine = (d: DescribedChange): string => (isVisibleChange(d)
      ? `<div class="cf-tl__change">
        <span class="cf-tl__field">${esc(d.label)}</span>
        <span class="cf-tl__from">${esc(d.from)}</span>
        <span class="cf-tl__arrow" aria-hidden="true">\u2192</span>
        <span class="cf-tl__to">${esc(d.to)}</span></div>`
      : '');

    const changeRows = commit.changes.map((change) => {
      /*
       * A `legs` change is a whole array, and becomes one line per leg that
       * actually changed — the old per-split rows, rebuilt from the field
       * that replaced them (ADR-0026).
       */
      if (change.entity === 'transaction' && change.field === 'legs') {
        return describeLegsChange(change, ctx).map(changeLine).join('');
      }
      // The split's currency as of this commit, so `amount` is rendered in
      // the money it is actually denominated in.
      const splitCurrency = change.entity === 'split'
        ? scalarText(snapshot.splits.get(change.recordId)?.['currencyCode']?.v)
        : undefined;
      return changeLine(describeChange(change, ctx, splitCurrency || undefined));
    }).join('');

    // Should be impossible \u2014 `beforeGroupCommit` rejects an unbalanced
    // transaction \u2014 so if it ever shows, it is a data-integrity finding
    // rather than a user error.
    const balanceWarning = unbalanced
      ? `<div class="cf-tl__warn">\u26a0 splits summed to ${esc(balanceOf(snapshot))}, not zero</div>`
      : '';

    // No show/hide toggle: the diff is the content, and hiding two or three
    // lines behind a click saved almost no space while costing a click on
    // every entry.

    /**
     * Viewing and restoring are gated differently on purpose.
     *
     * Looking at a past version writes nothing, so it is offered to anyone who
     * can read the record — including while someone else holds the lease,
     * which is exactly when you are most likely to be asking what happened.
     * Restoring is an edit and needs what an edit needs.
     *
     * Whether a restore is even possible is decided here rather than on
     * click: a version that cannot be restored should say so up front.
     */
    // `history.splits` carries the removed ones, so a version whose split
    // somebody deleted plans a restore rather than reporting itself
    // unreachable.
    const plan = opts.canRestore && !isCurrent
      ? restorePlan(snapshot, '', opts.currentSplits ?? [], opts.history.splits ?? [])
      : undefined;
    const actions = isCurrent ? '' : `<div class="cf-tl__actions">
      <button type="button" class="btn btn-sm btn-outline-secondary" data-tl-view="${commit.id}">${
        isViewing ? 'Showing this version' : 'View as of here'}</button>
      ${!opts.canRestore ? '' : plan?.ok
        ? `<button type="button" class="btn btn-sm btn-outline-primary" data-tl-restore="${commit.id}">Restore this version</button>`
        : '<span class="text-secondary" style="font-size:.7rem">Cannot restore: this version had a split '
          + 'this device knows nothing about.</span>'}
    </div>`;

    return `<div class="cf-tl ${isCurrent ? 'is-current' : ''} ${commit.source === 'import' ? 'is-import' : ''} ${isViewing ? 'is-viewing' : ''}">
      <span class="cf-tl__dot"></span>
      <div class="cf-tl__who">${who} ${currentChip} ${viewingChip} ${originChip} ${staleChip}</div>
      <div class="cf-tl__when">${esc(timeAgo(commit.wallTime))} \u00b7 ${esc(new Date(commit.wallTime).toLocaleString())}</div>
      <div class="cf-tl__summary">${esc(summariseCommit(commit, ctx))}</div>
      ${changeRows ? `<div class="cf-tl__changes">${changeRows}</div>` : ''}
      ${balanceWarning}
      ${actions}
    </div>`;
  }).join('');

  return `${revisions}${entries}`;
}

/** A split as it stands right now — restoring has to compare-and-swap against these. */
export interface CurrentSplit { id: string; version: number }

export type RestorePlan =
  | { ok: true; payload: Record<string, unknown> }
  /**
   * The version contained a split that is neither in the transaction now nor
   * in the history's own list of its splits — so there is no version to
   * write against, and a write that cannot be version-checked is not a write
   * this app makes (invariant #2).
   *
   * This used to be the answer for any removed split at all, which made a
   * version you could *look at* one you could not go back to. A group commit
   * now has a `restore` op, so the ordinary case — somebody deleted a split,
   * you want the version that had it — goes through. What is left here is
   * genuinely missing information, not a missing operation.
   */
  | { ok: false; reason: 'split-removed-since' };

/**
 * A snapshot as the payload the update endpoint takes, so "restore this
 * version" is an ordinary edit rather than a new server capability: it writes
 * a new commit with the old values, and the restore is itself visible in the
 * timeline afterwards (invariant #7, history is append-only — nothing is
 * rewritten).
 *
 * `baseVersion` for each split comes from its *current* version, not from the
 * snapshot: the write has to CAS against what is there now, or it would be
 * rejected as stale. Omitting them was the first thing that went wrong here —
 * the endpoint requires one for every `op: 'update'` child and answered
 * `invalid_request`.
 */
export function restorePlan(
  snapshot: Snapshot, txId: string, currentSplits: readonly CurrentSplit[],
  /**
   * Versions for splits that are not in `currentSplits` because they have
   * been removed — `TransactionHistory.splits`. Without it the old
   * behaviour stands and a version with a removed split cannot be restored.
   */
  knownSplits: ReadonlyArray<{ id: string; version: number; deleted: boolean }> = [],
): RestorePlan {
  const currentById = new Map(currentSplits.map((s) => [s.id, s]));
  const removedById = new Map(
    knownSplits.filter((s) => s.deleted && !currentById.has(s.id)).map((s) => [s.id, s]),
  );
  for (const id of snapshot.splits.keys()) {
    if (!currentById.has(id) && !removedById.has(id)) {
      return { ok: false, reason: 'split-removed-since' };
    }
  }

  const value = (fields: Record<string, FieldValue>, name: string): unknown => {
    const field = fields[name];
    return field === undefined || field.t === 'null' ? null : field.v;
  };

  const splits: Array<Record<string, unknown>> = [...snapshot.splits.entries()].map(([id, fields]) => ({
    id,
    // A split that is still here is updated; one that was removed since is
    // brought back and then updated, which the server does as one operation.
    op: currentById.has(id) ? 'update' : 'restore',
    baseVersion: (currentById.get(id) ?? removedById.get(id)!).version,
    accountId: value(fields, 'accountId'),
    memo: value(fields, 'memo') ?? '',
    value: scalarString(value(fields, 'value')) || '0',
    amount: scalarString(value(fields, 'amount')) || '0',
    currencyCode: value(fields, 'currencyCode'),
  }));

  // A split that exists now but not in the snapshot was added after it, so
  // restoring means removing it again.
  for (const current of currentSplits) {
    if (!snapshot.splits.has(current.id)) splits.push({ id: current.id, op: 'delete' });
  }

  return {
    ok: true,
    payload: {
      id: txId,
      txDate: value(snapshot.fields, 'txDate'),
      reference: value(snapshot.fields, 'reference') ?? '',
      description: value(snapshot.fields, 'description') ?? '',
      currencyCode: value(snapshot.fields, 'currencyCode'),
      splits,
    },
  };
}

/** Transactions per bulk history request — matches the endpoint's own cap. */
export const HISTORY_BATCH = 200;

/**
 * A cached history, tagged with the transaction version it was fetched at.
 *
 * That tag is what makes priming free in the steady state: a transaction's
 * history can only change when the transaction does, so a cached entry whose
 * `atVersion` still matches the list needs no request at all.
 */
export interface CachedHistory extends TransactionHistory { atVersion: number }
